// src/services/indexedDB/repositories/captureRepository.ts
// Capture 專屬邏輯

import { Capture } from '../../../types/capture';
import { BaseRepository } from '../core/base-repository';
import { STORE_NAMES, initDB } from '../core/database';
import { CachedEntity } from '../core/types';
import { logError, logInfo } from '../../../utils/logger';

/**
 * Capture with processedNum for indexing
 * IndexedDB 不支持布林值索引，需要轉換為數字
 */
interface CaptureWithProcessedNum extends Capture {
  processedNum?: number;
}

/**
 * Capture Repository
 * 處理 Capture 相關的 IndexedDB 操作
 */
class CaptureRepository extends BaseRepository<Capture> {
  protected readonly storeName = STORE_NAMES.CAPTURES;

  /**
   * 準備 Capture 以儲存（添加 processedNum）
   */
  protected prepareForStorage(entity: Capture): CachedEntity<Capture> {
    const prepared = super.prepareForStorage(entity) as CachedEntity<CaptureWithProcessedNum>;

    // 將布林值轉為數字以支持索引
    prepared.processedNum = entity.processed ? 1 : 0;

    // 序列化額外的日期欄位
    if (entity.convertedAt) {
      (prepared as any).convertedAt =
        entity.convertedAt instanceof Date ? entity.convertedAt.getTime() : entity.convertedAt;
    }

    return prepared as CachedEntity<Capture>;
  }

  /**
   * 反序列化 Capture（移除 processedNum 並轉換日期）
   */
  protected deserialize(stored: CachedEntity<Capture>): Capture {
    const { cachedAt, processedNum, ...entity } = stored as CachedEntity<CaptureWithProcessedNum>;

    // 轉換日期欄位
    const result: any = {
      ...entity,
      createdAt:
        typeof entity.createdAt === 'number' ? new Date(entity.createdAt) : entity.createdAt,
      updatedAt: entity.updatedAt
        ? typeof entity.updatedAt === 'number'
          ? new Date(entity.updatedAt)
          : entity.updatedAt
        : new Date(entity.createdAt), // 如果沒有 updatedAt，使用 createdAt
    };

    // 轉換 convertedAt（可選）
    if ((stored as any).convertedAt) {
      result.convertedAt =
        typeof (stored as any).convertedAt === 'number'
          ? new Date((stored as any).convertedAt)
          : (stored as any).convertedAt;
    }

    return result as Capture;
  }

  /**
   * 獲取未處理的 Captures
   */
  async getUnprocessed(userId: string, limit: number = 20): Promise<Capture[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_processedNum');

      return new Promise((resolve, reject) => {
        // 使用數字 0 表示未處理 (processed: false)
        const request = index.getAll([userId, 0]);

        request.onsuccess = () => {
          const results = request.result
            .slice(0, limit)
            .map((item: CachedEntity<Capture>) => this.deserialize(item));

          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to get unprocessed captures', error);
      throw error;
    }
  }

  /**
   * 按 TimeBox ID 獲取 Captures
   */
  async getByTimeBoxId(userId: string, timeBoxId: string): Promise<Capture[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId_timeBoxId');

      return new Promise((resolve, reject) => {
        const request = index.getAll([userId, timeBoxId]);

        request.onsuccess = () => {
          const results = request.result.map((item: CachedEntity<Capture>) =>
            this.deserialize(item)
          );
          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to get captures by TimeBox ID', error);
      throw error;
    }
  }

  /**
   * 獲取所有 Captures（按創建時間降序排列）
   * 重載以支持排序選項
   */
  async getAll(
    userId: string,
    options?: {
      limit?: number;
      offset?: number;
      sortBy?: 'createdAt' | 'updatedAt';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<Capture[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          let results = request.result.map((item: CachedEntity<Capture>) =>
            this.deserialize(item)
          );

          // 排序
          if (options?.sortBy) {
            const sortField = options.sortBy;
            results.sort((a, b) => {
              const aTime = a[sortField] ? new Date(a[sortField]!).getTime() : 0;
              const bTime = b[sortField] ? new Date(b[sortField]!).getTime() : 0;

              return options.sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
            });
          } else {
            // 預設按 createdAt 降序
            results.sort((a, b) => {
              const aTime = new Date(a.createdAt).getTime();
              const bTime = new Date(b.createdAt).getTime();
              return bTime - aTime;
            });
          }

          // 應用 offset 和 limit
          if (options?.offset) {
            results = results.slice(options.offset);
          }
          if (options?.limit) {
            results = results.slice(0, options.limit);
          }

          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('Failed to get all captures', error);
      throw error;
    }
  }

  /**
   * 同步 IndexedDB 與 Firestore
   * 刪除在 IndexedDB 中存在但在 Firestore 中不存在的項目
   */
  async syncWithFirestore(
    userId: string,
    firestoreIds: string[]
  ): Promise<{ synced: number; deleted: number }> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          const localCaptures = request.result;
          const firestoreIdSet = new Set(firestoreIds);

          // 找出在 IndexedDB 中但不在 Firestore 中的項目（需要刪除）
          const capturesToDelete: string[] = [];

          localCaptures.forEach((localCapture: any) => {
            if (!firestoreIdSet.has(localCapture.id)) {
              capturesToDelete.push(localCapture.id);
            }
          });

          if (capturesToDelete.length === 0) {
            resolve({ synced: localCaptures.length, deleted: 0 });
            return;
          }

          logInfo('檢測到 IndexedDB 殘留項目，開始清理', { count: capturesToDelete.length });

          // 執行刪除
          let deletedCount = 0;
          const deletePromises = capturesToDelete.map((captureId) => {
            return new Promise<void>((resolveDelete, rejectDelete) => {
              const deleteRequest = store.delete(captureId);
              deleteRequest.onsuccess = () => {
                deletedCount++;
                resolveDelete();
              };
              deleteRequest.onerror = () => rejectDelete(deleteRequest.error);
            });
          });

          Promise.all(deletePromises)
            .then(() => {
              logInfo('IndexedDB 同步完成', { deleted: deletedCount });
              resolve({
                synced: localCaptures.length - deletedCount,
                deleted: deletedCount,
              });
            })
            .catch(reject);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError('同步 IndexedDB 與 Firestore 失敗', error);
      throw error;
    }
  }
}

// 導出單例
export const captureRepository = new CaptureRepository();
